// ✅ FileUpload.jsx
import * as React from "react";
import { styled } from "@mui/material/styles";
import { Box, Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { colorPink, colorPrimary, textColor } from "../../Styles/Colors";
import FileUploaded from "../Custome/FileUploaded";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import Icon from "./Icon";
const Container = styled("div")({
  width: "100%",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "transparent",
  border: `2px dashed ${colorPrimary}`,
  borderRadius: "8px",
});

const UploadButton = styled(Button)({
  display: "flex",
  width: "100%",
  height: "100%",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
  lineHeight: "1.5",
  textTransform: "none",
  color: textColor,
  boxShadow: "none",
  backgroundColor: "transparent",
  padding: 0,

  "&:hover": {
    backgroundColor: "transparent",
    boxShadow: "none",
  },
});

const ErrorMessage = styled(Typography)({
  color: colorPink || "red",
  fontSize: "14px",
  marginTop: "8px",
});

const FileDetails = styled(Typography)({
  marginTop: "5px",
  fontSize: "14px",
  lineHeight: "1.5",
  color: textColor,
});

const VisuallyHiddenInput = styled("input")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});

export default function FileUpload({
  onChange,
  accept,
  maxSize,
  title,
  info,
  variant = "default",
  node,
}) {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [file, setFile] = React.useState(null);
  const [error, setError] = React.useState(null);

  const isError = variant === "error";
  const variantStyles = {
    container: isError
      ? {
          backgroundColor: "rgba(255,240,240,0.9)",
          border: `2px dashed ${colorPink || "rgba(211,47,47,0.8)"}`,
        }
      : {
          backgroundColor: "transparent",
          border: `2px dashed ${colorPrimary}`,
        },
    iconColor: isError ? colorPink || "#d32f2f" : colorPrimary,
    buttonColor: isError ? colorPink || "#d32f2f" : textColor,
  };

  const validateFile = (selectedFile) => {
    if (!selectedFile) return false;

    if (accept && !accept.includes(selectedFile.type)) {
      setError(`File type not allowed. Allowed: ${accept.join(", ")}`);
      return false;
    }
    if (maxSize && selectedFile.size > maxSize) {
      setError(`File size exceeds limit of ${maxSize / 1000000} MB.`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      onChange?.(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      onChange?.(droppedFile);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    setUploading(true);
    let progressValue = 0;
    const interval = setInterval(() => {
      progressValue = Math.min(progressValue + Math.random() * 10, 100);
      setProgress(progressValue);
      if (progressValue === 100) {
        clearInterval(interval);
        setUploading(false);
      }
    }, 100);
  };

  return (
    <>
      {!file && (
        <Container
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          sx={{
            ...variantStyles.container,
            borderRadius: "4px",
             ...node?.style,
            boxSizing: "border-box",
            padding: "24px",

            border:
              variant === "default"
                ? "1px dashed #83727C"
                : "1px dashed #BA1A1A",
            background:
              variant === "default"
                ? "rgba(33, 25, 30, 0.08)"
                : "rgba(186, 26, 26, 0.08)",
          }}
        >
          {!uploading ? (
            <>
              <UploadButton
               
                component="label"
                variant="contained"
                tabIndex={-1}
                sx={{
                  gap:"10px"
                }}
              >
                {node?.icn1 ? (
                  <Icon
                    style={{
                      ...node?.icn1?.style,
                      color:
                        variant === "default"
                          ? node?.icn1?.style?.color
                          : "#BA1A1A",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "transparent",
                  
                      }}
                      dangerouslySetInnerHTML={{ __html: node?.icn1?.icon }}
                    />
                  </Icon>
                ) : (
                  <UploadFileOutlinedIcon
                    sx={{
                      color: variantStyles.iconColor,
                      height: "44px",
                      width: "44px",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    ...node?.str1?.style,
                    color:
                      variant === "default"
                        ? node?.str1?.style?.color
                        : "#BA1A1A",
                  }}
                >
                  {node?.str1?.label || "Click or drag here to upload resume"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: textColor, fontSize: 14, ...node?.str2?.style,color: variant === "default" ? node?.str2?.style?.color : "#21191E" }}
                >
                  {node?.str2?.label ||
                    "Accepted file formats: .pdf, .docx, .doc, .rtf__"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: textColor, fontSize: 14, ...node?.str3?.style }}
                >
                  {node?.str3?.label || "Max file size: 5 MBs"}
                </Typography>
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileChange}
                  accept={accept?.join(",")}
                />
              </UploadButton>

              {error && <ErrorMessage>{error}</ErrorMessage>}
            </>
          ) : (
            <>
              <LinearProgress variant="determinate" value={progress} />
              <FileDetails>
                {file?.name} ({Math.round(progress)}%)
              </FileDetails>
            </>
          )}
        </Container>
      )}
      {file && (
        <FileUploaded
          fileName={file?.name}
          size={file?.size || 0}
          setFile={setFile}
        />
      )}
    </>
  );
}

FileUpload.propTypes = {
  onChange: PropTypes.func,
  accept: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
  title: PropTypes.string,
  info: PropTypes.string,
  variant: PropTypes.oneOf(["default", "error"]),
};
